/**
 * DatesAndNotesSection — invoice date, due date, PO ref, customer
 * note, internal note. Section 3 of the composer per
 * `11-new-invoice-screen-spec.md` §8.
 *
 * Visual layout:
 *   - 3-column grid: Invoice date · Due date · PO reference
 *   - 2-column grid below: Customer note (visible) · Internal note
 *     (private). The visual distinction lives in the helper text +
 *     a small "Visible" / "Internal" pill — the prototype's pattern.
 *
 * Due date suggestion: when the operator changes payment terms (the
 * parent's responsibility), the parent recomputes due date from
 * `issuedOn + termDays`. This component only renders + emits — the
 * parent handles the dependent recalculation so the rule stays in
 * one place.
 */
import { Card, CardBody, Input, Label } from "@flint/ui";
import { SectionHeading } from "./section-heading.tsx";

/** Red asterisk for required fields. Matches the prototype's `.req` span. */
function RequiredMark() {
  return (
    <span aria-hidden className="ml-0.5 text-rose-600" title="Required">
      *
    </span>
  );
}

/** Returns "Today, 13 May 2026" when `iso` is today's date; else `null`. */
function todayHelper(iso: string): string | null {
  const today = new Date().toISOString().slice(0, 10);
  if (iso !== today) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  // toLocaleDateString with `en-IN` produces "13/05/2026" — the
  // prototype's form is "Today, 6 May 2026", so we hand-format.
  const day = d.getUTCDate();
  const month = d.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
  const year = d.getUTCFullYear();
  return `Today, ${day} ${month} ${year}`;
}

export interface DatesAndNotesSectionProps {
  issuedOn: string;
  dueOn: string;
  poReference: string;
  customerNote: string;
  internalNote: string;
  declaration: string;
  /** Pre-fill text shown as a placeholder when the per-invoice declaration
   *  is empty — typically the company's default declaration. Visual hint
   *  only; the operator's typed value is what we send to the wire. */
  declarationPlaceholder?: string | undefined;
  /** Document noun used in labels/placeholders. Defaults to invoice. */
  documentLabel?: string | undefined;
  title?: string | undefined;
  description?: string | undefined;
  issuedLabel?: string | undefined;
  dueLabel?: string | undefined;
  poHelperText?: string | undefined;
  customerNotePlaceholder?: string | undefined;
  customerNoteVisibilityLabel?: string | undefined;
  declarationVisibilityLabel?: string | undefined;
  onChange: (
    patch: Partial<{
      issuedOn: string;
      dueOn: string;
      poReference: string;
      customerNote: string;
      internalNote: string;
      declaration: string;
    }>,
  ) => void;
  /** Helper text shown beneath the due-date field (e.g. "Auto from net 15"). */
  dueHint?: string | undefined;
  /** Validation error shown beneath the due-date field (red). Mirrors the
   *  `requireDueOnAfterIssuedOn` server-side Zod refinement so the operator
   *  catches it without round-trip. */
  dueError?: string | null | undefined;
  disabled?: boolean | undefined;
}

export function DatesAndNotesSection({
  issuedOn,
  dueOn,
  poReference,
  customerNote,
  internalNote,
  declaration,
  declarationPlaceholder,
  documentLabel = "invoice",
  title = "Dates, terms & notes",
  description = "When it's due, what to tell the customer, what to keep internal.",
  issuedLabel,
  dueLabel,
  poHelperText,
  customerNotePlaceholder,
  customerNoteVisibilityLabel,
  declarationVisibilityLabel,
  onChange,
  dueHint,
  dueError,
  disabled,
}: DatesAndNotesSectionProps) {
  return (
    <Card>
      <SectionHeading
        step={3}
        title={title}
        description={description}
      />
      <CardBody className="flex flex-col gap-3.5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="issued-on">
              {issuedLabel ?? `${capitalize(documentLabel)} date`}
              <RequiredMark />
            </Label>
            <Input
              id="issued-on"
              type="date"
              value={issuedOn}
              onChange={(e) => onChange({ issuedOn: e.target.value })}
              disabled={disabled}
              className="font-mono"
              required
            />
            {todayHelper(issuedOn) ? (
              <p className="text-sm text-muted">{todayHelper(issuedOn)}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="due-on">
              {dueLabel ?? "Due date"}
              <RequiredMark />
            </Label>
            <Input
              id="due-on"
              type="date"
              value={dueOn}
              onChange={(e) => onChange({ dueOn: e.target.value })}
              disabled={disabled}
              aria-invalid={dueError ? true : undefined}
              className={`font-mono ${dueError ? "border-rose-500 focus:ring-rose-500" : ""}`}
              required
            />
            {dueError ? (
              <p className="text-sm text-rose-700" role="alert">
                {dueError}
              </p>
            ) : dueHint ? (
              <p className="text-sm text-muted">{dueHint}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="po-ref">PO reference (optional)</Label>
            <Input
              id="po-ref"
              type="text"
              maxLength={64}
              value={poReference}
              onChange={(e) => onChange({ poReference: e.target.value })}
              disabled={disabled}
              placeholder="Customer PO number…"
            />
            <p className="text-sm text-muted">
              {poHelperText ?? `Helps the customer match this ${documentLabel} to their order.`}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="customer-note">
              Customer note{" "}
              <span className="text-sm text-muted font-normal">
                · {customerNoteVisibilityLabel ?? `appears on ${documentLabel}`}
              </span>
            </Label>
            <textarea
              id="customer-note"
              value={customerNote}
              maxLength={2000}
              rows={3}
              onChange={(e) => onChange({ customerNote: e.target.value })}
              disabled={disabled}
              placeholder={
                customerNotePlaceholder ??
                `Visible to the customer on the printed / WhatsApp ${documentLabel}...`
              }
              className="px-3 py-2 text-sm rounded-md border border-line bg-bg focus:outline-none focus:ring-1 focus:ring-line resize-none"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="internal-note">
              Internal note <span className="text-sm text-muted font-normal">· private</span>
            </Label>
            <textarea
              id="internal-note"
              value={internalNote}
              maxLength={2000}
              rows={3}
              onChange={(e) => onChange({ internalNote: e.target.value })}
              disabled={disabled}
              placeholder="Visible only to your team, never to the customer…"
              className="px-3 py-2 text-sm rounded-md border border-line bg-bg focus:outline-none focus:ring-1 focus:ring-line resize-none"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5 mt-1">
          <Label htmlFor="declaration">
            Declaration{" "}
            <span className="text-sm text-muted font-normal">
              · {declarationVisibilityLabel ?? `appears on ${documentLabel} PDF`}
            </span>
          </Label>
          <textarea
            id="declaration"
            value={declaration}
            maxLength={4000}
            rows={4}
            onChange={(e) => onChange({ declaration: e.target.value })}
            disabled={disabled}
            placeholder={
              declarationPlaceholder ||
              "One declaration per line."
            }
            className="px-3 py-2 text-sm rounded-md border border-line bg-bg focus:outline-none focus:ring-1 focus:ring-line resize-y font-mono"
          />
          <p className="text-sm text-muted">
            One statement per line — the PDF renders these as a numbered list. Prefilled from your
            company default; edit to override for this invoice only.
          </p>
        </div>
      </CardBody>
    </Card>
  );
}

function capitalize(value: string): string {
  return value.length === 0 ? value : `${value[0]!.toUpperCase()}${value.slice(1)}`;
}
